import {APIPostClassroomsMany} from "../../../classrooms/api/classrooms";
import {IClassroom} from "../../../../shared/types/Classroom";
import {mapStudentOption} from "../utils/functions";
import {ITeacher, IUser} from "../../../../shared/types/Users";
import {niceUserName, niceUserNameWithSubjects} from "../../../../utils/functions";
import React, {Dispatch, useCallback} from "react";
import {APIGetClassroom} from "../../../classrooms/api/classroom";
import {useAuth} from "../../../../hooks/useAuth";
import {SelectChangeEvent} from "@mui/material/Select";

interface useClassroomSelectionOptions {
    users: any[];
    dispatch: Dispatch<any>;
    setLoading: Dispatch<any>;
}

const useClassroomSelection = ({users, dispatch, setLoading}: useClassroomSelectionOptions) => {
    const { update } = useAuth();

    const setSimpleClassroomHandler = (_event: any, value: any) => {
        dispatch({
            type: 'classroom',
            payload: value,
        })
    };

    const setClassroomHandler = useCallback(async (_event: unknown, value: { id: number } | null) => {
        dispatch({
            type: "classroom",
            payload: value,
        });

        if (!value?.id) {
            dispatch({ type: "users", payload: [] });
            return;
        }

        setLoading({ loading: true, name: "students" });

        try {
            const response = await APIGetClassroom({ id: value.id });

            if (response.data?.success) {
                const rawStudents: IUser[] = response.data.data.item.students ?? [];
                const students = rawStudents
                    .filter((student) => student.email_verified_at !== null)
                    .map(mapStudentOption);

                dispatch({
                    type: "users",
                    payload: students,
                });
            } else {
                update("error", { show: true, code: "A002" });
            }
        } catch {
            update("error", { show: true, code: "A001" });
        } finally {
            setLoading({ loading: false, name: "students" });
        }
    }, [dispatch, update, setLoading]);

    const setClassroomsHandler = (_event: any, value: any) => {
        dispatch({
            type: 'classrooms',
            payload: value,
        })
    };

    const setSelectedClassroomsStudentsHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {name, checked} = event.target;
        const checkedStudents = users.map((item: any) => {
            return {
                ...item,
                students: item.students.map((student: any) => {
                    if (student.value.toString() === name) {
                        return {...student, checked: checked};
                    }
                    return student;
                })
            }
        })
        dispatch({
            type: 'users',
            payload: checkedStudents,
        });
    };

    const getSelectedClassrooms = (classrooms: any) => {
        const selectedClassroomsIds = classrooms.map((classroom: any) => classroom.id);
        setLoading({loading: true, name: "students"});
        APIPostClassroomsMany({ids: selectedClassroomsIds})
            .then((response) => {
                if (response.data.success) {
                    let classrooms = response.data.data.items.map(
                        (item: IClassroom) => {
                            return {
                                ...item,
                                id: item.id,
                                start_date: new Date(),
                                students: item.students
                                    .filter((student: {
                                        email_verified_at: string | null
                                    }) => student.email_verified_at !== null) // Filter students
                                    .map(
                                        (student: IUser) => {
                                            return {
                                                label: niceUserName(student.first_name, student.last_name, student.email),
                                                value: student.id.toString(),
                                                checked: true,
                                                ...student,
                                            };
                                        }
                                    ),
                                teachers: item.teachers
                                    .filter((teacher: {
                                        email_verified_at: string | null
                                    }) => teacher.email_verified_at !== null) // Filter teachers
                                    .map(
                                        (teacher: ITeacher) => {
                                            return {
                                                label: niceUserNameWithSubjects(teacher.first_name, teacher.last_name, teacher.subjects, teacher.email),
                                                value: teacher.id.toString(),
                                                checked: false,
                                                ...teacher,
                                            };
                                        }
                                    )
                            };
                        }
                    );
                    dispatch({
                        type: 'users',
                        payload: classrooms,
                    });
                } else update("error", {show: true, code: "A002"});
            })
            .catch(() => update("error", {show: true, code: "A001"}))
            .finally(() => setLoading({loading: false, name: "students"}));
    };

    const setSelectedStudentsHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checkedStudents = users.map((item) => {
            if (item.value.toString() === event.target.name) {
                return {...item, checked: event.target.checked};
            }
            return item;
        });

        dispatch({
            type: 'users',
            payload: checkedStudents,
        });
    };

    const setSelectAllStudentsHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checkedStudents = users.map((item: any) => {
            return {...item, checked: event.target.checked};
        });

        dispatch({
            type: 'users',
            payload: checkedStudents,
        })
    };

    const setTeacherHandler = (e: SelectChangeEvent) => {
        const classroomId = parseInt(e.target.name.split('_')[1]);
        let checkedTeachers = users.map((item: any) => {
            return {
                ...item,
                teachers: item.teachers.map((teacher: any) => {
                    if (classroomId === item.id) {
                        if (teacher.value.toString() === e.target.value) {
                            return {
                                ...teacher,
                                checked: true,
                            };
                        } else {
                            return {
                                ...teacher,
                                checked: false,
                            };
                        }
                    } else {
                        return teacher;
                    }

                }),
            }
        });
        dispatch({
            type: 'users',
            payload: checkedTeachers,
        });
    };

    return {
        setSimpleClassroomHandler,
        setClassroomHandler,
        setClassroomsHandler,
        setSelectedClassroomsStudentsHandler,
        getSelectedClassrooms,
        setSelectedStudentsHandler,
        setSelectAllStudentsHandler,
        setTeacherHandler,
    }
}

export default useClassroomSelection;